/* =========================================================================
 * "CONNECT YOUR GMAIL" — the one control that makes per-rep outreach real.
 *
 * Brian: "They all must be logged into their own hubspot as well... for
 * internal, they login to their Gmail and hubspot for the pipeline. It should
 * pass through our system."
 *
 * ⚠️ THIS CARD IS DELIBERATELY LOUD ABOUT WHOSE MAILBOX IT IS. The failure it
 * exists to prevent is a rep drafting into somebody else's outbox, and the only
 * way a rep can catch that themselves is if the address is on screen. So the
 * bound address is shown whenever there is one — connected or mismatched — and
 * never summarised to "Connected ✓".
 *
 * ⚠️ AND IT NEVER SAYS "NOT CONNECTED" WHEN IT MEANS "COULD NOT TELL". The
 * status route returns a reason with every false, and this renders that reason
 * rather than collapsing every one of them into the same grey dot. "You have not
 * connected yet", "you connected the wrong account", "this deploy has no OAuth
 * client" and "we could not reach the server" need four different actions from
 * four different people.
 * ========================================================================= */

import { useState, useEffect, useCallback } from 'react'
import { api, proseOf } from '../../lib/api.js'
import styles from './championToClose.module.css'

/* The failure copy and the never-an-object guarantee live in lib so they can
 * be tested without a DOM. See src/lib/repConnectError.js for the measured
 * 409 this exists to render, and verify-connect-error-prose.mjs for the gate.
 */
import { failureSentence } from '../../lib/repConnectError.js'

/* What the rep is actually agreeing to, in words rather than scope URLs. A
 * consent screen a rep cannot read is a consent they cannot give. */
const WHAT_WE_ASK = [
  ['Create drafts in your Gmail', 'so the outreach lands in your own drafts folder, under your name.'],
  ['See your email address', 'so this account can be tied to you and nobody else.'],
]

export default function RepGmailConnect({ onStatus }) {
  const [state, setState] = useState({ loading: true })
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (signal) => {
    try {
      const r = await api.repGmailStatus({ signal })
      const next = { loading: false, configured: r.configured, grant: r.grant || {}, scopes: r.scopes || [] }
      setState(next)
      onStatus?.(next)
    } catch (err) {
      if (err?.name === 'AbortError') return
      /* ⚠️ AN UNREACHABLE STATUS ROUTE IS NOT A DISCONNECTED REP. Rendering it
       * as "not connected" would send fifteen people to re-consent an account
       * that was fine, and hide the fact that the API is down. */
      const next = { loading: false, unknown: true,
        detail: `Could not read your Gmail connection (${err?.status || 'network'}). This does NOT mean you are disconnected — it means we could not tell.` }
      setState(next)
      onStatus?.(next)
    }
  }, [onStatus])

  useEffect(() => {
    const c = new AbortController()
    load(c.signal)
    /* Re-check when the rep comes back from the Google tab. The consent happens
     * in another window, so nothing here would otherwise know it succeeded and
     * the rep would be looking at a stale "not connected" card after doing
     * exactly what it asked. */
    const onFocus = () => load()
    window.addEventListener('focus', onFocus)
    return () => { c.abort(); window.removeEventListener('focus', onFocus) }
  }, [load])

  const connect = async () => {
    setBusy(true)
    /* Clear any previous failure first, so a second click that succeeds does not
     * leave the last reason sitting under a working button. */
    setState((s) => ({ ...s, failure: '' }))
    try {
      const r = await api.repGmailConnect()
      if (r?.url) {
        const w = window.open(r.url, '_blank', 'noopener')
        /* ⚠️ A BLOCKED POPUP IS ALSO A SILENT FAILURE. The link was minted and
         * is good; the browser simply refused the window. Say so and hand the
         * rep the link rather than letting the click look like it did nothing. */
        if (!w) setState((s) => ({ ...s, failure: 'Your browser blocked the Google window. Allow pop-ups for this site, or use the direct link below.', url: r.url }))
      } else {
        setState((s) => ({ ...s, failure: 'The server did not return a Google consent link, so there is nothing to open. Nothing was changed on your account.' }))
      }
    } catch (err) {
      /* ⚠️ THE ONE LINE THIS WHOLE FIX IS ABOUT. `failureSentence` is
       * guaranteed to return a STRING, so this can never again put an object
       * into state for the renderer to choke on. */
      setState((s) => ({ ...s, failure: failureSentence(err) }))
    } finally { setBusy(false) }
  }

  const disconnect = async () => {
    setBusy(true)
    try { await api.repGmailDisconnect(); await load() }
    finally { setBusy(false) }
  }

  if (state.loading) return <div className={styles.gmailCard}>Checking your Gmail connection…</div>

  const g = state.grant || {}
  const connected = !!g.connected

  return (
    <div className={`${styles.gmailCard} ${connected ? styles.gmailOk : styles.gmailWarn}`}>
      <div className={styles.gmailHead}>
        <span className={styles.gmailDot} aria-hidden="true">{connected ? '✓' : '○'}</span>
        <span className={styles.gmailTitle}>
          {connected ? 'Drafting into your own mailbox' : 'Your Gmail is not connected yet'}
        </span>
      </div>

      {/* THE ADDRESS, ALWAYS, whenever there is one. */}
      {g.email && (
        <div className={styles.gmailAddr}>
          {connected ? 'Drafts will appear in ' : 'Currently connected as '}
          <b>{g.email}</b>
        </div>
      )}

      {/* The reason, verbatim from the server. `proseOf` so a nested object can
          never reach the renderer and take the card down with it. */}
      {!connected && (proseOf(state.detail) || proseOf(g.detail)) && (
        <p className={styles.gmailWhy}>{proseOf(state.detail) || proseOf(g.detail)}</p>
      )}

      {/* ⚠️ THE FAILED CLICK, SAID OUT LOUD. role="alert" so a rep who is not
          looking at this corner of the screen is still told. This block is the
          difference between "nothing happened" and an answer. */}
      {proseOf(state.failure) && (
        <p className={styles.gmailWhy} role="alert">
          <b>Couldn’t start the connection.</b> {proseOf(state.failure)}
          {state.url && (
            <>
              {' '}
              <a href={state.url} target="_blank" rel="noopener noreferrer">Open the Google consent screen</a>
            </>
          )}
        </p>
      )}

      {!connected && !state.unknown && (
        <>
          <ul className={styles.gmailScopes}>
            {WHAT_WE_ASK.map(([what, why]) => (
              <li key={what}><b>{what}</b> — {why}</li>
            ))}
          </ul>
          {/* ⚠️ STATED PLAINLY, BECAUSE A REP IS ABOUT TO GRANT ACCESS TO THEIR
              OWN MAILBOX AND IS ENTITLED TO KNOW THE LIMIT. It is also true:
              the backend has one Gmail endpoint and it is drafts.create. */}
          <p className={styles.gmailNever}>
            Southern Recognition cannot send mail as you and cannot read your inbox.
            Every outreach is a draft you open and send yourself.
          </p>
        </>
      )}

      <div className={styles.gmailActions}>
        {connected ? (
          <button type="button" className="btn" disabled={busy} onClick={disconnect}>Disconnect</button>
        ) : (
          <button type="button" className="btn btn-primary" disabled={busy || state.unknown} onClick={connect}>
            {busy ? 'Opening Google…' : 'Connect my Gmail'}
          </button>
        )}
        {state.unknown && (
          <button type="button" className="btn" disabled={busy} onClick={() => load()}>Try again</button>
        )}
      </div>
    </div>
  )
}
